import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { ArrowRight, ArrowLeft, Check, ImagePlus, Plus, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { AuthField, OnboardingWizard, LearnerOnboardingWizard, IoLearnerOnboarding } from 'components/auth';
import { IS_IO } from 'lib/site';
import { FocusPicker, RolePicker, UseCasePicker, YourPicks, pruneForUseCase } from 'components/auth/org-onboarding';
import { detectRecipient, type Recipient } from 'components/organization/InviteMembersModal';
import { useCompleteOnboarding } from 'modules/auth';
import { useAuthContext } from 'modules/auth/context';
import {
  COMPANY_SIZES,
  type AssessArea,
  type GradeBand,
  type OnboardingRole,
  type OrgUseCase,
  type PrimaryGoal,
} from 'modules/auth/onboarding';
import type { CompanySize } from 'modules/auth/types';
import * as Organization from 'modules/organization';
import { useToast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import config from 'config';

const makeOrgSchema = (t: TFunction) =>
  z.object({
    fullName: z.string().min(2, t('auth.enter_full_name')),
    companyName: z.string().min(2, t('auth.enter_company_name')),
    companySize: z
      .string()
      .refine((v) => (COMPANY_SIZES as readonly string[]).includes(v), t('auth.select_company_size')),
  });
type OrgFormData = z.infer<ReturnType<typeof makeOrgSchema>>;

// Step indexes. Use case and role are required; from Focus on the wizard can be skipped.
const STEP = { ORG: 0, USE_CASE: 1, ROLE: 2, FOCUS: 3, INVITE: 4 } as const;

// Local preview of the workspace slug the server derives from the org name.
// Display-only — onboarding has no slug field; createOrgWithUniqueSlug owns it.
const slugify = (s: string): string =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32);

const INVITE_ROLE = 'AUTHOR';

function StepNav({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between pt-2">
      <Button type="button" variant="outline" onClick={onBack}>
        <ArrowLeft className="mr-2 h-4 w-4" />
        {t('back')}
      </Button>
      <Button type="button" className="group" onClick={onNext}>
        {t('continue')}
        <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
      </Button>
    </div>
  );
}

const StaffOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { state, methods } = useAuthContext();
  const user = state.user;

  const completeOnboarding = useCompleteOnboarding();

  const [wizardStep, setWizardStep] = React.useState(0);
  const [useCase, setUseCase] = React.useState<OrgUseCase | null>(null);
  const [role, setRole] = React.useState<OnboardingRole | null>(null);
  const [areas, setAreas] = React.useState<AssessArea[]>([]);
  const [gradeBand, setGradeBand] = React.useState<GradeBand | null>(null);
  const [goal, setGoal] = React.useState<PrimaryGoal | null>(null);
  const [showUseCaseError, setShowUseCaseError] = React.useState(false);
  const [showRoleError, setShowRoleError] = React.useState(false);
  // After a successful submit the wizard ends on "Your picks" instead of jumping to /app.
  const [finished, setFinished] = React.useState(false);
  // Captured at mount: someone who reopens /onboarding after finishing goes to the app
  // instead of resubmitting. (profileComplete flips mid-submit, so it can't be read live.)
  const [arrivedComplete] = React.useState(() => user?.profileComplete === true);
  const [inviteDraft, setInviteDraft] = React.useState('');
  const [recipients, setRecipients] = React.useState<Recipient[]>([]);

  // Phone self-serve users carry a masked-phone placeholder; only they must type
  // a real name here. Google users arrive with a real name — prefill and hide it.
  const hasRealName = Boolean(user?.name && !user.name.includes('*'));
  const needsName = !hasRealName;

  const orgForm = useForm<OrgFormData>({
    resolver: zodResolver(makeOrgSchema(t)),
    defaultValues: { fullName: hasRealName ? (user?.name ?? '') : '', companyName: '', companySize: '' },
  });

  const orgName = orgForm.watch('companyName');
  const slug = slugify(orgName || '');

  // A brand-new Google signup can mount this page before the profile fetch
  // resolves, so the real name arrives AFTER the form initialized `fullName` to
  // ''. Seed it once known: otherwise `needsName` flips false (field unmounts)
  // while RHF still holds '', and goNext()'s trigger() silently fails on the
  // now-hidden required field — dead-ending the user on step 1 with no error.
  React.useEffect(() => {
    if (hasRealName && user?.name) {
      orgForm.setValue('fullName', user.name, { shouldValidate: false });
    }
  }, [hasRealName, user?.name, orgForm]);

  const goBack = () => setWizardStep((s) => Math.max(0, s - 1));

  const chooseUseCase = (next: OrgUseCase) => {
    setUseCase(next);
    setShowUseCaseError(false);
    // Switching use case must not submit answers the new one no longer shows.
    const kept = pruneForUseCase(next, { role, areas, gradeBand });
    setRole(kept.role);
    setAreas(kept.areas);
    setGradeBand(kept.gradeBand);
  };

  const goNext = async () => {
    if (wizardStep === STEP.ORG) {
      const ok = await orgForm.trigger();
      if (ok) setWizardStep(STEP.USE_CASE);
      return;
    }
    if (wizardStep === STEP.USE_CASE) {
      if (!useCase) {
        setShowUseCaseError(true);
        return;
      }
      setWizardStep(STEP.ROLE);
      return;
    }
    if (wizardStep === STEP.ROLE) {
      if (!role) {
        setShowRoleError(true);
        return;
      }
      setWizardStep(STEP.FOCUS);
      return;
    }
    if (wizardStep === STEP.FOCUS) setWizardStep(STEP.INVITE);
  };

  const addRecipient = () => {
    const rec = detectRecipient(inviteDraft);
    if (!rec) return;
    setRecipients((prev) => (prev.some((r) => r.value === rec.value) ? prev : [...prev, rec]));
    setInviteDraft('');
  };

  const handleComplete = async (sendInvites: boolean) => {
    const data = orgForm.getValues();
    try {
      await completeOnboarding.mutateAsync({
        fullName: data.fullName.trim(),
        companyName: data.companyName.trim(),
        companySize: data.companySize as CompanySize,
        useCase: useCase ?? undefined,
        role: role ?? undefined,
        assessAreas: areas.length ? areas : undefined,
        gradeBand: gradeBand ?? undefined,
        primaryGoal: goal ?? undefined,
      });

      if (sendInvites && recipients.length) {
        const orgId = localStorage.getItem(config.api.active_org_key) || state.organization?.id;
        const emails = recipients.filter((r) => r.kind === 'email');
        const phones = recipients.filter((r) => r.kind === 'phone');
        if (orgId && emails.length) {
          // Best-effort: a failed invite must not block finishing onboarding.
          await Promise.allSettled(
            emails.map((r) => Organization.Api.SendEmailInvite(orgId, { email: r.value, role: INVITE_ROLE })),
          );
        }
        // Org invites are email-only today; surface phones honestly instead of
        // silently dropping them.
        if (phones.length) {
          toast.info(t('auth.ob_phone_invite_later'), { description: t('auth.ob_phone_invite_later_desc') });
        }
      }

      setFinished(true);
    } catch (error) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(t('error'), {
        description: err?.response?.data?.message || t('auth.could_not_complete_onboarding'),
      });
    }
  };

  const steps = [
    t('auth.ob_step_org'),
    t('auth.ob_step_usecase'),
    t('auth.ob_step_role'),
    t('auth.ob_step_focus'),
    t('auth.ob_step_invite'),
  ];
  const busy = completeOnboarding.isPending;

  if (arrivedComplete) return <Navigate to="/app" replace />;

  if (finished) {
    return (
      <OnboardingWizard steps={steps} current={steps.length}>
        <YourPicks useCase={useCase} />
      </OnboardingWizard>
    );
  }

  return (
    <OnboardingWizard
      steps={steps}
      current={wizardStep}
      onSkip={wizardStep >= STEP.FOCUS ? () => handleComplete(false) : undefined}
    >
      {/* Step 1 — Organization */}
      {wizardStep === STEP.ORG && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('auth.ob_org_title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth.ob_org_desc')}</p>
          </div>

          {/* Logo — optional, not persisted yet (honest placeholder) */}
          <div className="flex items-center gap-4">
            <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-dashed border-border text-muted-foreground">
              <ImagePlus className="h-6 w-6" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{t('auth.ob_org_logo')}</p>
              <p className="text-xs text-muted-foreground">{t('auth.ob_org_logo_hint')}</p>
            </div>
          </div>

          {needsName && (
            <AuthField
              type="text"
              autoComplete="name"
              label={t('full_name')}
              placeholder={t('john_doe')}
              error={orgForm.formState.errors.fullName?.message}
              {...orgForm.register('fullName')}
            />
          )}

          <AuthField
            type="text"
            autoComplete="organization"
            label={t('auth.ob_org_name')}
            placeholder={t('acme_inc')}
            error={orgForm.formState.errors.companyName?.message}
            {...orgForm.register('companyName')}
          />

          {/* Workspace URL — a live preview of the server-derived slug (display only) */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground/90">{t('auth.ob_workspace_url')}</Label>
            <div className="flex h-11 items-center overflow-hidden rounded-lg border border-border bg-muted/40 text-sm">
              <span className="select-none border-r border-border px-3 text-muted-foreground">examing.uz/</span>
              <span className="flex-1 truncate px-3 font-mono text-foreground">
                {slug || t('auth.ob_workspace_placeholder')}
              </span>
              {slug ? <Check className="mr-3 h-4 w-4 shrink-0 text-green-600 dark:text-green-500" aria-hidden /> : null}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-foreground/90">{t('auth.company_size')}</Label>
            <Controller
              name="companySize"
              control={orgForm.control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger aria-invalid={orgForm.formState.errors.companySize ? true : undefined}>
                    <SelectValue placeholder={t('auth.company_size_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map((size) => (
                      <SelectItem key={size} value={size}>
                        {size} {t('auth.employees')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {orgForm.formState.errors.companySize ? (
              <p className="text-xs font-medium text-destructive">
                {orgForm.formState.errors.companySize.message}
              </p>
            ) : null}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="button" className="group" onClick={goNext}>
              {t('continue')}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2 — Use case (required) */}
      {wizardStep === STEP.USE_CASE && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('auth.ob_usecase_title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth.ob_usecase_desc')}</p>
          </div>

          <UseCasePicker value={useCase} onChange={chooseUseCase} />
          {showUseCaseError ? (
            <p role="alert" className="text-xs font-medium text-destructive">{t('auth.ob_usecase_required')}</p>
          ) : null}

          <StepNav onBack={goBack} onNext={goNext} />
        </div>
      )}

      {/* Step 3 — Role (required; options follow the use case) */}
      {wizardStep === STEP.ROLE && useCase && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('auth.ob_role_title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth.ob_role_desc')}</p>
          </div>

          <RolePicker
            useCase={useCase}
            value={role}
            onChange={(next) => {
              setRole(next);
              setShowRoleError(false);
            }}
          />
          {showRoleError ? (
            <p role="alert" className="text-xs font-medium text-destructive">{t('auth.ob_usecase_required')}</p>
          ) : null}

          <StepNav onBack={goBack} onNext={goNext} />
        </div>
      )}

      {/* Step 4 — Focus (optional) */}
      {wizardStep === STEP.FOCUS && useCase && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('auth.ob_focus_title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth.ob_focus_desc')}</p>
          </div>

          <FocusPicker
            useCase={useCase}
            areas={areas}
            onAreasChange={setAreas}
            gradeBand={gradeBand}
            onGradeBandChange={setGradeBand}
            goal={goal}
            onGoalChange={setGoal}
          />

          <StepNav onBack={goBack} onNext={goNext} />
        </div>
      )}

      {/* Step 5 — Invite group */}
      {wizardStep === STEP.INVITE && (
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('auth.ob_invite_title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('auth.ob_invite_desc')}</p>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground/90">{t('auth.ob_invite_field')}</Label>
            <div className="flex gap-2">
              <Input
                value={inviteDraft}
                onChange={(e) => setInviteDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                onBlur={addRecipient}
                placeholder={t('auth.ob_invite_placeholder')}
                className="h-11 flex-1"
              />
              <Button
                type="button"
                variant="outline"
                className="h-11 shrink-0"
                onClick={addRecipient}
                aria-label={t('auth.ob_add_recipient', 'Add recipient')}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {recipients.length > 0 && (
              <div className="divide-y divide-border rounded-lg border border-border">
                {recipients.map((r) => (
                  <div key={r.value} className="flex items-center gap-3 px-4 py-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold uppercase text-muted-foreground">
                      {r.value.slice(0, 2)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{r.value}</p>
                      <p className="text-xs text-muted-foreground">{t('auth.ob_invite_as_author')}</p>
                    </div>
                    <span className="shrink-0 rounded-md bg-muted px-2 py-0.5 text-xs font-semibold text-foreground">
                      {t('auth.ob_role_author')}
                    </span>
                    <button
                      type="button"
                      onClick={() => setRecipients((prev) => prev.filter((x) => x.value !== r.value))}
                      className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={t('remove')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button type="button" variant="outline" onClick={goBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back')}
            </Button>
            <Button type="button" className="group" onClick={() => handleComplete(true)} disabled={busy}>
              {busy ? t('auth.finishing') : t('auth.finish_setup')}
              {!busy && <Check className="ml-2 h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      {/* Escape hatch (kept from the moderation gate): abandon + sign out. */}
      <div className="mt-8 border-t border-border pt-4 text-center">
        <button
          type="button"
          onClick={() => {
            methods.logout();
            navigate('/login', { replace: true });
          }}
          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {t('auth.sign_out')}
        </button>
      </div>
    </OnboardingWizard>
  );
};

const Onboarding: React.FC = () => {
  const { state } = useAuthContext();
  const user = state.user;

  // If user is a student / candidate (default), render the interactive 6-step questionnaire
  if (user?.accountType !== 'STAFF') {
    // examing.io learners only need a name: no exam goals, study plan or plan offer there.
    return IS_IO ? <IoLearnerOnboarding /> : <LearnerOnboardingWizard />;
  }

  // Teachers / Staff continue through their school / org setup wizard
  return <StaffOnboarding />;
};

export default Onboarding;
